// src/pages/Register.jsx - With Return to Checkout Flow
import { useState, useContext } from "react";
import { UserContext } from "../components/UserContext";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Form, Button, Container, Card, Alert } from "react-bootstrap";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { getCsrfToken } from "../utils/csrf";

const Register = () => {
  const { login } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirmPassword: "", subscribeNewsletter: true });
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [registrationComplete, setRegistrationComplete] = useState(false);

  // Check if user came from checkout
  const returnToCheckout = location.state?.returnToCheckout;

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    if (password.length < 8) return false;
    if (!/(?=.*[a-z])/.test(password)) return false; // lowercase
    if (!/(?=.*[A-Z])/.test(password)) return false; // uppercase
    if (!/(?=.*\d)/.test(password)) return false; // number
    if (!/(?=.*[@$!%*?&])/.test(password)) return false; // special char
    return true;
  };

  const handleEmailChange = (e) => {
    const value = e.target.value.toLowerCase().trim();
    setForm({ ...form, email: value });
    
    if (value && !validateEmail(value)) {
      setValidationErrors({ ...validationErrors, email: "Invalid email format" });
    } else {
      const { email, ...rest } = validationErrors;
      setValidationErrors(rest);
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setForm({ ...form, password: value });

    if (value && !validatePassword(value)) {
      setValidationErrors({ ...validationErrors, password: "Password must be at least 8 characters with uppercase, lowercase, number, and special character (@$!%*?&)" });
    } else {
      const { password, ...rest } = validationErrors;
      setValidationErrors(rest);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    const errors = {};

    if (!validateEmail(form.email)) {
      errors.email = "Invalid email format";
    }

    if (!validatePassword(form.password)) {
      errors.password = "Password must be at least 8 characters with uppercase, lowercase, number, and special character (@$!%*?&)";
    }

    if (form.password !== form.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setError("Please fix the validation errors");
      return;
    }

    setLoading(true);

    try {
      const csrfToken = await getCsrfToken();
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken
        },
        credentials: "include",
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          password: form.password,
          subscribeNewsletter: form.subscribeNewsletter
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setRegistrationComplete(true);
        setMessage("Account created! Please check your email to verify your account. Check the server console for the verification link.");
      } else {
        // Show detailed validation errors if available
        if (data.details && data.details.length > 0) {
          const errorMessages = data.details.map(err => err.msg).join('. ');
          setError(errorMessages);
        } else {
          setError(data.error || "Registration failed. Please try again.");
        }
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (registrationComplete) {
    return (
      <>
        <Navbar />
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
          <Card style={{ width: "100%", maxWidth: "450px" }} className="shadow-lg">
            <Card.Body className="p-5 text-center">
              <div className="mb-4" style={{ fontSize: "48px" }}>✉️</div>
              <h2 className="mb-4" style={{ color: "#003366", fontWeight: "700" }}>
                Verify Your Email
              </h2>
              <Alert variant="success">{message}</Alert>
              <p className="text-muted mb-4">
                We've sent a verification link to <strong>{form.email}</strong>. 
                Please check your email and click the link to activate your account.
              </p>
              {returnToCheckout && (
                <Alert variant="info">
                  <strong>📦 Your cart is saved!</strong> After verifying your email, 
                  log in to continue with your order.
                </Alert>
              )}
              <div className="d-grid gap-2">
                <Button 
                  variant="outline-primary"
                  onClick={() => navigate("/login", { state: { returnToCheckout } })}
                  style={{ borderColor: "#003366", color: "#003366" }}
                >
                  Go to Login
                </Button>
                {returnToCheckout && (
                  <Button 
                    variant="outline-secondary"
                    onClick={() => navigate("/cart")}
                  >
                    Return to Cart
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
        <Card style={{ width: "100%", maxWidth: "450px" }} className="shadow-lg">
          <Card.Body className="p-5">
            <h1 className="text-center mb-4" style={{ color: "#003366", fontWeight: "700" }}>
              Create Account
            </h1>

            {returnToCheckout && (
              <Alert variant="info" className="mb-4">
                <strong>🎉 Great choice!</strong> Create your account to track this order and enjoy faster checkouts in the future.
              </Alert>
            )}

            {error && <Alert variant="danger">{error}</Alert>}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Full Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter your name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  value={form.email}
                  onChange={handleEmailChange}
                  isInvalid={!!validationErrors.email}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.email}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Password (min 8 characters)"
                  value={form.password}
                  onChange={handlePasswordChange}
                  isInvalid={!!validationErrors.password}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.password}
                </Form.Control.Feedback>
                <Form.Text className="text-muted small">
                  Must contain: 8+ characters, uppercase, lowercase, number, and special character (@$!%*?&)
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Confirm Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Confirm password"
                  value={form.confirmPassword}
                  onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                  isInvalid={!!validationErrors.confirmPassword}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.confirmPassword}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Check
                  type="checkbox"
                  id="subscribeNewsletter"
                  label="Subscribe to our newsletter for exclusive offers and product updates"
                  checked={form.subscribeNewsletter}
                  onChange={(e) => setForm({ ...form, subscribeNewsletter: e.target.checked })}
                />
              </Form.Group>

              <div className="d-grid gap-2">
                <Button 
                  variant="primary" 
                  type="submit" 
                  disabled={loading}
                  style={{ backgroundColor: "#003366", border: "none", fontWeight: "600" }}
                >
                  {loading ? "Creating Account..." : "Create Account"}
                </Button>

                {returnToCheckout && (
                  <Button 
                    variant="outline-secondary"
                    onClick={() => navigate("/cart")}
                  >
                    Back to Cart
                  </Button>
                )}
              </div>
            </Form>

            <p className="text-center mt-4 mb-0">
              Already have an account?{" "}
              <Link 
                to="/login" 
                state={{ returnToCheckout }}
                style={{ color: "#003366", fontWeight: "600" }}
              >
                Login here
              </Link>
            </p>
          </Card.Body>
        </Card>
      </Container>
      <Footer />
    </>
  );
};

export default Register;